import type { MetaSchemaField } from "../../../components/MetaSchemaEditor/MetaSchemaEditor";
import type { SchemaField } from "../../../components/SchemaEditor/SchemaEditor";
import {
  checkPayload,
  payloadProblemText,
} from "../../projects/config/payloadSchema";
import type { HelpRequestType } from "../../projects/config/types";
import type {
  BotsConfig,
  BotPlayRhythm,
  BotProfile,
  BotProfileWeightedValue,
} from "../config/types";
import type { BoardShape } from "./scoreMode";
// The account-age ceiling, shared with the two dialogs that hold a number of
// days to it — see MAX_AGE_DAYS.
import { MAX_AGE_DAYS } from "../roster";

/**
 * Client-side validation of the bots section, mirroring what
 * `bot.Parse` refuses on the backend at PATCH.
 *
 * The two lists have to agree, and that is the whole point of the duplication.
 * The backend rejects the save on any of about twenty rules; if the form only
 * checked two of them, Save would look enabled, the request would come back
 * 400, and the operator would be left with a generic failure and no idea which
 * field caused it. So every rule the server has is checked here too, and the
 * ones it cannot express per field — the profile shares adding up, the
 * time-of-day bands tiling the day — are checked over the whole table.
 *
 * Neither failure is loud at runtime. A table summing to 90 quietly never
 * produces a tenth of the pool; an uncovered hour quietly stops the simulation
 * every night at four. Refusing the save is the only moment anybody can still
 * be told.
 */

/** One reason the section cannot be saved, in the same shape the API returns. */
export interface BotsProblem {
  path: string;
  message: string;
}

export interface BotsErrors {
  /** Whole-table message under the profile editor, if any. */
  profiles?: string;
  /** Whole-table message under the time-of-day editor, if any. */
  timeOfDay?: string;
  /**
   * What the save bar says when a payload template does not match the schema of
   * the type it is filed under.
   *
   * The one problem on this page whose CAUSE is in another document: the schema
   * is edited on the Config page, so the row the operator is looking at can be
   * correct-looking and still refused. The banner lists it like every other
   * problem — this is the sentence beside the greyed-out button, so the reason
   * is on screen even when the row has scrolled away.
   */
  helpPayload?: string;
  /** Indexes of the profile rows to mark invalid. */
  profileRows: number[];
  /**
   * Why a profile's play rhythm cannot be saved, keyed by the row's index in
   * `profiles` — the ranges to paint and the one sentence under the grid. A
   * row absent from the map has a rhythm with nothing wrong.
   */
  playRows: Record<number, BotPlayRowError>;
  /** Why a growing stat cannot be saved, keyed by its index in `growth`. */
  growthRows: Record<number, BotGrowthRowError>;
  /** Why a board cannot follow its stat, keyed by the board's index in
   *  `leaderboards`: the stat, and the rest of the sentence after it. */
  scoreStatRows: Record<number, { stat: string; reason: string }>;
  /** Indexes of the band rows to mark invalid. */
  bandRows: number[];
  /** Gaps and overlaps, as rows the editor prints between the bands. */
  gaps: BandGap[];
  /**
   * Why a `bots.profile` rule cannot be saved, keyed by its index in
   * `profile.stats` / `profile.meta`.
   *
   * By index rather than by key because the index is what the path in
   * `problems` names, and the editor already knows which rule each of its rows
   * holds. A row absent from the map is a row with nothing wrong.
   */
  profileStats: Record<number, BotProfileRowError>;
  profileMeta: Record<number, BotProfileRowError>;
  /**
   * Why `bots.profile.avatar` cannot be saved, if it cannot.
   *
   * A field of its own rather than a slot in one of the two maps above: the
   * avatar is not a card key, so it has neither a key to be found by nor an
   * index to be numbered with — there is exactly one of it. Absent is a rule
   * with nothing wrong, which is also every project that has no rule at all.
   */
  profileAvatar?: BotProfileRowError;
  /**
   * Everything else, path and all — the per-field rules the server also
   * enforces. Listed rather than attached to inputs so the section can print
   * them together, exactly as the API would return them.
   */
  problems: BotsProblem[];
}

/** One range of a play rhythm. Doubles as the last segment of its problem's
 *  path, and as the stem of its two keys (`days_per_week_min` / `_max`). */
export type BotPlayField = "days_per_week" | "sessions_per_day" | "session_minutes";

/** What the Play rhythm grid calls each range, in its column headers and in
 *  the sentences about them. */
export const PLAY_FIELD_LABELS: Record<BotPlayField, string> = {
  days_per_week: "days a week",
  sessions_per_day: "sessions a day",
  session_minutes: "session length",
};

/** Why one row of the Play rhythm grid cannot be saved. */
export interface BotPlayRowError {
  /** The ranges to paint red. */
  fields: BotPlayField[];
  /** The first reason, as the line under the grid prints it. */
  message: string;
}

/** One box of a Growing stats row. Doubles as the last segment of its
 *  problem's path. */
export type BotGrowthField = "key" | "mode" | "gain" | "every" | "cap";

/** Why one row of the Growing stats grid cannot be saved. */
export interface BotGrowthRowError {
  /** The boxes to paint red. */
  fields: BotGrowthField[];
  /** The first reason, as the line under the grid prints it. */
  message: string;
}

/** The interval bounds of a during-play row, in minutes, as the server holds
 *  them — a week at most. */
export const EVERY_BOUNDS = { lo: 1, hi: 10080 };

/** Which box of a profile row an error belongs to. Doubles as the last
 *  segment of the problem's path, so the two can never drift apart. */
export type BotProfileField =
  | "key"
  | "start"
  | "true_percent"
  | "count"
  | "values";

/** Why one row of the profile editor cannot be saved. */
export interface BotProfileRowError {
  /** Printed under the row — normally exactly one line. */
  messages: string[];
  /** The boxes to paint. */
  fields: BotProfileField[];
  /**
   * Values the key's allow-list no longer admits. Kept apart from `messages`
   * because the row prints the offending value as code and paints its chip,
   * which is more than a sentence can carry — and because the value is NOT
   * removed: existing bots keep it until the pool is re-rolled.
   */
  outside: string[];
}

/**
 * The parts of the PROJECT's own config these settings are read against —
 * `games.config`, the second document the server parses them alongside.
 *
 * `stats` / `meta` are what a bot's profile rules describe how to fill;
 * `helpTypes` is what a help kind names and, since a type may declare a
 * `payload_schema`, what its payload templates have to satisfy.
 *
 * Optional at the call site — without them the ranges are still checked, only
 * the allow-lists and the schemas cannot be, since nothing says what a key
 * admits.
 */
export interface ProjectSchemas {
  stats: SchemaField[];
  meta: MetaSchemaField[];
  helpTypes?: HelpRequestType[];
  /** The project's boards — whose operator decides whether one can follow a
   *  stat at all. */
  boards?: BoardShape[];
}

/** One stretch of the day that no band covers, or that two bands do. */
export interface BandGap {
  from: string;
  to: string;
  kind: "gap" | "overlap";
  /** Index of the band this row is printed above. */
  before: number;
}

const MINUTES_PER_DAY = 24 * 60;

/** Parse "HH:MM" into minutes since midnight; null when it is not a clock. */
export function parseClock(value: string): number | null {
  const m = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const hh = Number(m[1]);
  const mm = Number(m[2]);
  if (hh < 0 || hh > 24 || mm < 0 || mm > 59) return null;
  const total = hh * 60 + mm;
  return total > MINUTES_PER_DAY ? null : total;
}

export function formatClock(minutes: number): string {
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;
  return `${String(hh).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

/** Sum of the profile shares — shown next to the editor either way. */
export function sharesTotal(profiles: BotProfile[]): number {
  return profiles.reduce((n, p) => n + (Number.isFinite(p.share) ? p.share : 0), 0);
}

export function validateBots(
  bots: BotsConfig,
  schemas?: ProjectSchemas,
): BotsErrors {
  const out: BotsErrors = {
    profileRows: [],
    playRows: {},
    growthRows: {},
    scoreStatRows: {},
    bandRows: [],
    gaps: [],
    profileStats: {},
    profileMeta: {},
    problems: [],
  };
  const add = (path: string, message: string) =>
    out.problems.push({ path, message });
  const percent = (path: string, v: number) => {
    if (!(v >= 0 && v <= 100)) add(path, `${v} is not a percentage between 0 and 100`);
  };
  const nonNegative = (path: string, v: number) => {
    if (!(v >= 0)) add(path, "must not be negative");
  };
  const positive = (path: string, v: number) => {
    if (!(v > 0)) add(path, "must be greater than zero");
  };
  const range = (path: string, lo: number, hi: number) => {
    if (lo < 0) add(path, "must not be negative");
    else if (hi < lo) add(path, "maximum is below minimum");
  };

  // --- basics ---
  if (bots.default_locale.trim() === "") {
    add("bots.default_locale", "a project needs a language its bots fall back to");
  }
  for (const [country, locale] of Object.entries(bots.locale_by_country)) {
    if (country.trim().length !== 2 || locale.trim() === "") {
      add(
        "bots.locale_by_country",
        `“${country}” → “${locale}” is not a country code mapped to a locale`,
      );
    }
  }

  // --- population ---
  const pop = bots.population;
  nonNegative("bots.population.fill_below_members", pop.fill_below_members);
  nonNegative("bots.population.kick_refill_pause_hours", pop.kick_refill_pause_hours);
  positive("bots.population.target_members", pop.target_members);
  positive("bots.population.target_members_min", pop.target_members_min);
  if (pop.target_members_min > pop.target_members) {
    add(
      "bots.population.target_members_min",
      `the smallest guild ${pop.target_members_min} cannot be larger than the largest ${pop.target_members}`,
    );
  }
  percent("bots.population.max_bot_share", pop.max_bot_share);
  percent("bots.population.join_probability", pop.join_probability);
  percent("bots.population.leave_probability", pop.leave_probability);
  if (pop.target_members < pop.fill_below_members) {
    add(
      "bots.population.target_members",
      `target size ${pop.target_members} is below the fill threshold ${pop.fill_below_members} — bots would join and leave forever`,
    );
  }

  // --- backfill ---
  positive("bots.backfill.depth_hours", bots.backfill.depth_hours);
  nonNegative("bots.backfill.min_messages", bots.backfill.min_messages);
  if (bots.backfill.max_messages < bots.backfill.min_messages) {
    add(
      "bots.backfill.max_messages",
      `maximum ${bots.backfill.max_messages} is below minimum ${bots.backfill.min_messages}`,
    );
  }

  // --- newcomer boost ---
  if (bots.newcomer_boost.speed_up < 1) {
    add(
      "bots.newcomer_boost.speed_up",
      `${bots.newcomer_boost.speed_up} would make the first hour slower than the background, not faster`,
    );
  }
  percent(
    "bots.newcomer_boost.welcome_probability",
    bots.newcomer_boost.welcome_probability,
  );

  // --- simulation ---
  // Zero is legal and documented: it means "simulate round the clock".
  nonNegative(
    "bots.simulation.awake_window_minutes",
    bots.simulation.awake_window_minutes,
  );

  // --- help kinds ---
  const helpSchemas = new Map(
    (schemas?.helpTypes ?? []).map((t) => [t.type, t.payload_schema]),
  );
  const seenKind = new Set<string>();
  bots.help.kinds.forEach((k, i) => {
    const path = `bots.help.kinds[${i}]`;
    const type = k.request_type.trim();
    if (type === "") {
      add(`${path}.request_type`, "empty request type");
    } else if (seenKind.has(type)) {
      add(`${path}.request_type`, `“${type}” is listed twice`);
    }
    seenKind.add(type);
    nonNegative(`${path}.weight`, k.weight);
    positive(`${path}.max_helpers`, k.max_helpers);
    positive(`${path}.donate_amount`, k.donate_amount);
    range(`${path}.first_delay`, k.first_delay_min_seconds, k.first_delay_max_seconds);
    range(`${path}.next_delay`, k.next_delay_min_seconds, k.next_delay_max_seconds);
    // The templates against the schema of the type they are filed under — the
    // same check, in the same words, that the server makes of a game's own
    // payload. A type this project does not declare, or one that declares no
    // schema, has nothing to check against and says nothing, exactly as the
    // profile rules do when the project declares no card.
    const schema = helpSchemas.get(type);
    (k.payloads ?? []).forEach((tpl, j) => {
      const at = `${path}.payloads[${j}]`;
      nonNegative(`${at}.weight`, tpl.weight);
      const problems =
        typeof tpl.payload === "string"
          ? // The only way a string reaches this field: the operator is midway
            // through typing a payload and what they have is not JSON yet.
            [{ reason: "not_an_object" as const }]
          : checkPayload(tpl.payload, schema);
      for (const problem of problems) {
        add(at + ".payload", payloadProblemText(problem, schema));
        out.helpPayload ??= `A payload of “${type}” does not match its schema.`;
      }
    });
  });

  // --- guild limits, chat, names ---
  nonNegative("bots.guild_limits.messages_per_hour", bots.guild_limits.messages_per_hour);
  nonNegative("bots.guild_limits.gap_seconds", bots.guild_limits.gap_seconds);
  nonNegative("bots.guild_limits.no_repeat_last", bots.guild_limits.no_repeat_last);
  nonNegative("bots.guild_limits.phrase_reuse_hours", bots.guild_limits.phrase_reuse_hours);
  percent("bots.chat.thanks_probability", bots.chat.thanks_probability);
  nonNegative("bots.chat.max_reactions_per_2min", bots.chat.max_reactions_per_2min);
  nonNegative("bots.chat.small_talk_weight", bots.chat.small_talk_weight);
  range(
    "bots.chat.thanks_delay",
    bots.chat.thanks_delay_min_seconds,
    bots.chat.thanks_delay_max_seconds,
  );
  percent("bots.names.digits_suffix_percent", bots.names.digits_suffix_percent);

  // --- account age ---
  // The four things the server refuses about the range a bot's account age is
  // drawn from, in the order `parseAccountAge` refuses them — same conditions,
  // same branches, same box named. The ceiling is the one the Create guilds
  // dialog already holds a guild's age to and for the same arithmetic: past it
  // the duration overflows and dates the account in the FUTURE.
  const age = bots.account_age;
  nonNegative("bots.account_age.min_days", age.min_days);
  if (age.max_days < 0) {
    // Named on its own rather than left to «below minimum», which with BOTH
    // boxes negative does not fire at all: the range is not backwards, it is
    // simply impossible twice. An operator told about one of the two boxes
    // would fix that one and be refused again by the other.
    add("bots.account_age.max_days", "must not be negative");
  } else if (age.max_days < age.min_days) {
    add(
      "bots.account_age.max_days",
      `maximum ${age.max_days} is below minimum ${age.min_days}`,
    );
  } else if (age.max_days > MAX_AGE_DAYS) {
    // Bounding the top bounds the whole range — the branch above has already
    // established that the bottom is not above the top. Said as `else` rather
    // than as a second `if` so a range that runs backwards is one sentence and
    // not two about the same box.
    add(
      "bots.account_age.max_days",
      `${age.max_days} days is past the ${MAX_AGE_DAYS}-day ceiling`,
    );
  }

  // --- growing stats ---
  const growing = validateGrowth(bots, schemas, out);

  // --- leaderboards ---
  const seenBoard = new Set<string>();
  bots.leaderboards.forEach((l, i) => {
    const path = `bots.leaderboards[${i}]`;
    const key = l.ext_key.trim();
    if (key === "") {
      add(`${path}.ext_key`, "empty leaderboard key");
    } else if (seenBoard.has(key)) {
      add(`${path}.ext_key`, `“${key}” is listed twice`);
    }
    seenBoard.add(key);
    if (l.score_stat) {
      // A board that follows a stat saves no numbers of its own to check.
      const board = schemas?.boards?.find((b) => b.ext_key === key);
      const reason =
        board?.operator === "worst"
          ? "can't be followed on this board — it keeps the worse result"
          : board?.operator === "best" && board.sort === "asc"
            ? "can't be followed on this board — lower is better here"
            : !growing.has(l.score_stat)
              ? "no longer grows — pick another source"
              : undefined;
      if (reason) {
        out.scoreStatRows[i] = { stat: l.score_stat, reason };
        add(`${path}.score_stat`, `“${l.score_stat}” ${reason}`);
      }
      return;
    }
    // A cleared box is NaN, which JSON sends as null and the server replaces
    // with its own default, so both ends must be whole numbers.
    const submitsWhole =
      Number.isInteger(l.submits_per_day_min) &&
      Number.isInteger(l.submits_per_day_max);
    if (!submitsWhole) {
      add(`${path}.submits_per_day`, "“submits” needs a whole number at both ends");
    } else if (l.submits_per_day_max < l.submits_per_day_min) {
      add(`${path}.submits_per_day`, "maximum is below minimum");
    }
    if (
      !Number.isInteger(l.score_per_submit_min) ||
      !Number.isInteger(l.score_per_submit_max)
    ) {
      add(`${path}.score_per_submit`, "“score per submit” needs a whole number at both ends");
    } else if (l.score_per_submit_max < l.score_per_submit_min) {
      add(`${path}.score_per_submit`, "maximum is below minimum");
    }
    if (l.score_mode !== "result" && l.score_mode !== "gain") {
      add(`${path}.score_mode`, `“${l.score_mode}” is not one of result, gain`);
    }
    if (submitsWhole && l.enabled && l.submits_per_day_max <= 0) {
      add(`${path}.submits_per_day`, "enabled with no submits a day");
    }
  });

  // --- maturation ---
  nonNegative(
    "bots.maturation.leave_when_real_members",
    bots.maturation.leave_when_real_members,
  );
  nonNegative(
    "bots.maturation.sleep_when_active_players",
    bots.maturation.sleep_when_active_players,
  );

  // --- profiles: per row, then the whole table ---
  if (bots.profiles.length === 0) {
    out.profiles = "A bot with no profile cannot exist — add at least one.";
  } else {
    const total = sharesTotal(bots.profiles);
    if (total !== 100) {
      const missing = 100 - total;
      out.profiles =
        missing > 0
          ? `Shares add up to ${total}%. Add the missing ${missing}% or drop a profile — a bot with no profile cannot exist.`
          : `Shares add up to ${total}%. Remove ${-missing}% — the table has to be exactly 100%.`;
      out.profileRows = bots.profiles.map((_, i) => i);
    }
    const seen = new Set<string>();
    bots.profiles.forEach((p, i) => {
      const path = `bots.profiles[${i}]`;
      const key = p.key.trim();
      if (key === "" || seen.has(key)) {
        if (!out.profileRows.includes(i)) out.profileRows.push(i);
        out.profiles ??=
          key === ""
            ? "Every profile needs a key."
            : `“${key}” is listed twice.`;
      }
      seen.add(key);
      percent(`${path}.share`, p.share);
      percent(`${path}.message_probability`, p.message_probability);
      nonNegative(`${path}.max_messages_per_day`, p.max_messages_per_day);
      range(`${path}.interval`, p.min_interval_minutes, p.max_interval_minutes);
      range(`${path}.cooldown`, p.cooldown_min_minutes, p.cooldown_max_minutes);
      validatePlay(p, i, out);
    });
  }

  // --- time of day ---
  validateBands(bots, out);
  // --- profile rules ---
  // The avatar is checked apart from the two collections, and before them: the
  // server reads it before it looks at a schema, and it stands on a project
  // that declares none.
  validateAvatar(bots, out);
  validateProfile(bots, schemas, out);
  return out;
}

/**
 * `bots.growth[]` — each row a distinct stat, a gain that can reach 1, an
 * interval inside a week for a during-play row, and a whole cap no fresh bot
 * starts above. One reason per box, the first the server would give; the grid
 * prints the row's first one.
 *
 * A key the schema no longer declares as int is dropped by the server rather
 * than refused, so it is not refused here either — and it does not grow.
 * Returns the keys that do.
 */
function validateGrowth(
  bots: BotsConfig,
  schemas: ProjectSchemas | undefined,
  out: BotsErrors,
): Set<string> {
  const ints = new Set(
    (schemas?.stats ?? []).filter((f) => f.type === "int").map((f) => f.key.trim()),
  );
  const seen = new Set<string>();
  bots.growth.forEach((g, i) => {
    const path = `bots.growth[${i}]`;
    const key = g.key.trim();
    const who = key || `stat ${i + 1}`;
    const fail = (field: BotGrowthField, message: string) => {
      out.problems.push({ path: `${path}.${field}`, message });
      const row = (out.growthRows[i] ??= { fields: [], message: `${who}: ${message}.` });
      if (!row.fields.includes(field)) row.fields.push(field);
    };
    if (key === "") {
      fail("key", "pick the stat that grows");
      return;
    }
    if (schemas && !ints.has(key)) return;
    if (seen.has(key)) {
      fail("key", `“${key}” is listed twice`);
      return;
    }
    seen.add(key);
    if (g.mode !== "during_play" && g.mode !== "per_day") {
      fail("mode", `“${g.mode}” is not one of during_play, per_day`);
    }
    if (!Number.isInteger(g.gain_min) || !Number.isInteger(g.gain_max)) {
      fail("gain", "“gain” needs a whole number at both ends");
    } else if (g.gain_min < 0) {
      fail("gain", "“gain” must not be negative");
    } else if (g.gain_min > g.gain_max) {
      fail("gain", `“gain” starts above where it ends — ${g.gain_min} is more than ${g.gain_max}`);
    } else if (g.gain_max < 1) {
      fail("gain", "“gain” has to reach at least 1, or the stat never moves");
    }
    if (g.mode === "during_play") {
      const min = g.every_minutes_min;
      const max = g.every_minutes_max;
      const { lo, hi } = EVERY_BOUNDS;
      const label = "“every”";
      const outside = [min, max].find((v) => v < lo || v > hi);
      if (!Number.isInteger(min) || !Number.isInteger(max)) {
        fail("every", `${label} needs a whole number at both ends`);
      } else if (outside !== undefined) {
        fail("every", `${label} runs ${lo} to ${hi} min — ${outside} is outside it`);
      } else if (min > max) {
        fail("every", `${label} starts above where it ends — ${min} is more than ${max}`);
      }
    }
    const start = bots.profile.stats.find((r) => r.key === key)?.start_max;
    if (g.cap !== undefined) {
      if (!Number.isInteger(g.cap)) fail("cap", "“cap” needs a whole number");
      else if (g.cap < 0) fail("cap", "“cap” must not be negative");
      else if (start !== undefined && g.cap < start) {
        fail("cap", `the cap ${g.cap} is below the starting range — a freshly created bot would already be over it`);
      }
    }
  });
  return seen;
}

/** The bounds of each play range, as the server holds them — and the native
 *  min/max of the boxes that edit them. */
export const PLAY_BOUNDS: Record<BotPlayField, { lo: number; hi: number }> = {
  days_per_week: { lo: 1, hi: 7 },
  sessions_per_day: { lo: 1, hi: MINUTES_PER_DAY },
  session_minutes: { lo: 1, hi: MINUTES_PER_DAY },
};

/**
 * `bots.profiles[i].play` — present, each range whole numbers inside its
 * bounds and not backwards, and a play day that fits in a day.
 *
 * One reason per range, the first the server would give, so a box is never
 * described twice; the grid prints the row's first one.
 */
function validatePlay(p: BotProfile, i: number, out: BotsErrors) {
  const path = `bots.profiles[${i}].play`;
  const who = p.key.trim() || `profile ${i + 1}`;
  const fail = (fields: BotPlayField[], at: string, message: string) => {
    out.problems.push({ path: at, message });
    const row = (out.playRows[i] ??= { fields: [], message: `${who}: ${message}.` });
    for (const f of fields) if (!row.fields.includes(f)) row.fields.push(f);
  };
  const play = p.play;
  if (!play) {
    fail(
      ["days_per_week", "sessions_per_day", "session_minutes"],
      path,
      "no play rhythm — every profile needs one",
    );
    return;
  }
  const failed = new Set<BotPlayField>();
  for (const field of Object.keys(PLAY_BOUNDS) as BotPlayField[]) {
    const { lo, hi } = PLAY_BOUNDS[field];
    const min = play[`${field}_min`];
    const max = play[`${field}_max`];
    const label = `“${PLAY_FIELD_LABELS[field]}”`;
    const at = `${path}.${field}`;
    const outside = [min, max].find((v) => v < lo || v > hi);
    if (!Number.isInteger(min) || !Number.isInteger(max)) {
      fail([field], at, `${label} needs a whole number at both ends`);
    } else if (outside !== undefined) {
      fail([field], at, `${label} runs ${lo} to ${hi} — ${outside} is outside it`);
    } else if (min > max) {
      fail([field], at, `${label} starts above where it ends — ${min} is more than ${max}`);
    } else {
      continue;
    }
    failed.add(field);
  }
  // Only once all three ranges are sound, as the server does: a broken one
  // already has its own reason, and a product of its ends means nothing.
  const busiest = busiestDay(play);
  if (failed.size === 0 && busiest > MINUTES_PER_DAY) {
    fail(
      ["sessions_per_day", "session_minutes"],
      path,
      `${play.sessions_per_day_max} sessions of ${play.session_minutes_max} min is ${busiest} min — more than the ${MINUTES_PER_DAY} a day holds`,
    );
  }
}

/**
 * The midpoint of a play range, or null when the range cannot be saved —
 * the one number the «online / week» column multiplies.
 */
function playMid(play: BotPlayRhythm, field: BotPlayField): number | null {
  const { lo, hi } = PLAY_BOUNDS[field];
  const min = play[`${field}_min`];
  const max = play[`${field}_max`];
  if (!Number.isInteger(min) || !Number.isInteger(max)) return null;
  if (min < lo || min > max || max > hi) return null;
  return (min + max) / 2;
}

/** The most minutes a play day can take: its most sessions, each its longest. */
function busiestDay(play: BotPlayRhythm): number {
  return play.sessions_per_day_max * play.session_minutes_max;
}

/**
 * How long a bot of the profile is online in an average week, in minutes:
 * the midpoints of its three ranges multiplied. Null when the rhythm cannot
 * be saved — a broken range, a play day longer than a day — or is missing.
 */
export function onlineMinutesPerWeek(play: BotPlayRhythm | undefined): number | null {
  if (!play) return null;
  const days = playMid(play, "days_per_week");
  const sessions = playMid(play, "sessions_per_day");
  const minutes = playMid(play, "session_minutes");
  if (days === null || sessions === null || minutes === null) return null;
  if (busiestDay(play) > MINUTES_PER_DAY) return null;
  return days * sessions * minutes;
}

/** «≈ 7 h», «≈ 45 min», or «—» for a rhythm that cannot be saved. */
export function formatOnlineWeek(minutes: number | null): string {
  if (minutes === null) return "—";
  if (Math.round(minutes) < 60) return `≈ ${Math.round(minutes)} min`;
  return `≈ ${Math.round(minutes / 60)} h`;
}

const RANGE_BACKWARDS = "Lower bound is above the upper one.";
const NEGATIVE = "must not be negative";
const NO_VALUES = "no values to draw from";
const NEGATIVE_WEIGHT = "a weight cannot be negative";
const EMPTY_IN_LIST = "an empty value cannot be an element of a list";
/**
 * Every number in a rule is an integer on the wire (`*int64`, `*int`), and a
 * fraction does not decode into one: the field arrives at the server ABSENT,
 * so a box reading 1.5 on screen is stored as nothing at all — a range with a
 * missing bound, or a weight of zero. The box says one thing and the config
 * means another, which is the one outcome a save must not have.
 */
const NOT_WHOLE =
  "Whole numbers only — a fraction does not survive the trip, and the box arrives empty.";
/** A chance is what a bool rule IS; absent is not «never». */
const NO_CHANCE =
  "A chance is needed — a ticked key with none gives every bot “false”.";

/** The three named ranges, as the sentences below address them. */
const START_RANGE = "A starting range";
const COUNT = "A count";

/**
 * A range with a box left empty, in each of the two ways it can be left.
 *
 * An empty box is not "unset": it reads as zero on the server, and zero is a
 * real setting nobody typed. An unfinished `start` rolls zeros onto every bot —
 * the row of zeros this whole section exists to remove, written on purpose this
 * time; an unfinished `count` hands out empty lists to a project that wrote a
 * catalogue of badges. Neither says anything at runtime, so the save is the
 * last moment anybody can be told — and `bot.requirePair` refuses both, so a
 * save that got past here would come back 400 anyway.
 */
function missingBound(
  what: string,
  lo: number | undefined,
  hi: number | undefined,
): string | undefined {
  if (lo === undefined && hi === undefined) {
    return `${what} is needed — with neither bound the rule rolls zeros.`;
  }
  if (lo === undefined) return `${what} needs its lower bound too.`;
  if (hi === undefined) return `${what} needs its upper bound too.`;
  return undefined;
}

/** Every type `meta_schema` can declare has a rule form — the form follows the
 *  type, so all four are rolled and none is silently ignored. */
const META_RULE_TYPES: string[] = ["string", "string[]", "int", "bool"];

/**
 * What a stat can be rolled as. `string[]` is missing on purpose: the
 * platform's own validation of a player's stats does not know that type, so a
 * bot would be carrying a value the game itself could not write. `float` has
 * no form in either collection.
 */
const STAT_RULE_TYPES: string[] = ["int", "bool", "string"];

/**
 * Does this rule draw from a weighted pool? With the schema, the key's type
 * decides. Without it nothing can be typed, so the question becomes whether a
 * pool is there at all — an absent one must not be read as an empty one, which
 * is the single failure the check below treats as fatal.
 */
function hasPool(
  type: string | undefined,
  values: BotProfileWeightedValue[] | undefined,
  schemas: ProjectSchemas | undefined,
  ...poolTypes: string[]
): boolean {
  return schemas ? poolTypes.includes(type ?? "") : values !== undefined;
}

/** The rules every weighted pool obeys, wherever it is declared. */
function checkPool(
  values: BotProfileWeightedValue[],
  bad: (message: string) => void,
) {
  const weights = values.map((v) => v.weight);
  if (weights.some((w) => !Number.isInteger(w))) {
    // A weight is an `*int` too, so a fractional one arrives absent and the
    // value quietly becomes one that never comes up.
    bad(NOT_WHOLE);
  } else if (weights.some((w) => w < 0)) {
    bad(NEGATIVE_WEIGHT);
  } else if (weights.reduce((a, b) => a + b, 0) <= 0) {
    // Covers the empty list too: nothing to draw from either way.
    bad(NO_VALUES);
  }
}

/**
 * The rest of the sentence about a value the allow-list has dropped. Exported
 * so the row can print it after the value itself as code, while the problems
 * list quotes the value inline — one wording, two typographies.
 */
export const OUTSIDE_ALLOW_LIST =
  "is not in this key's allowed list any more. Existing bots keep it until the pool is re-rolled — the same rule the player editor follows, which never deletes a cosmetic quietly.";

/**
 * `bots.profile.avatar` — the one rule that is not a card key.
 *
 * It mirrors what `parseWeightedValues` refuses of the avatar list, and only
 * that: a weight cannot be negative, and a list nothing can be drawn from is
 * the operator's own numbers not adding up. Whole numbers too — a weight is an
 * `*int` on the wire, so a fraction arrives absent and the value quietly
 * becomes one that never comes up, which is the same reason every other pool
 * on this page is held to it.
 *
 * A value outside `profile.allowed_avatars` is NOT an error, here or on the
 * server. Refusing it at the save would redistribute its weight over the rest,
 * so a retired asset would quietly make every bot that would have worn it wear
 * something ELSE; dropping it where the bot is created does what the whitelist
 * actually says — that bot wears nothing.
 *
 * An ABSENT list is not a half-filled rule, so it is not read at all: «no
 * values to draw from» is a complaint about a rule somebody started, and every
 * project that has never heard of this key would otherwise raise it on every
 * save.
 */
function validateAvatar(bots: BotsConfig, out: BotsErrors) {
  // A config written before this key existed carries no list at all, and that
  // is the same statement an empty one makes: no rule.
  const values = bots.profile.avatar ?? [];
  if (values.length === 0) return;
  const row: BotProfileRowError = { messages: [], fields: [], outside: [] };
  checkPool(values, (message) => {
    row.messages.push(message);
    if (!row.fields.includes("values")) row.fields.push("values");
    out.problems.push({ path: "bots.profile.avatar.values", message });
  });
  if (row.messages.length > 0) out.profileAvatar = row;
}

/**
 * `bots.profile` — one pass per rule, marking the box and printing the reason
 * once, exactly as HelpTypesEditor does.
 *
 * The pass follows `bot.parseProfileRules` on the server, including what it
 * does NOT refuse: a rule naming a key the project no longer declares, or one
 * whose type has no rule form, is DROPPED there rather than rejected. Refusing
 * it here would freeze Save over a row the editor does not draw and the server
 * would have thrown away anyway. Which fields of a rule are read follows the
 * key’s type for the same reason — a range on a bool key is not config, it is
 * leftovers.
 *
 * Without the schemas nothing can be dropped or typed, so every field that is
 * present is checked instead — but nothing is ever REQUIRED there: which boxes
 * a rule owns is a question only its type answers, and a missing box on a rule
 * of unknown type is as likely to be a box that key never had.
 */
function validateProfile(
  bots: BotsConfig,
  schemas: ProjectSchemas | undefined,
  out: BotsErrors,
) {
  // Neither catalogue declared: `bot.parseProfileRules` returns before it
  // reads a single rule — there is no card to fill, so there is nothing a rule
  // could be checked against. Checking anyway would refuse the save over rows
  // the editor does not draw at all.
  if (schemas && schemas.stats.length === 0 && schemas.meta.length === 0) {
    return;
  }
  const rowOf = (bag: Record<number, BotProfileRowError>, i: number) => {
    let row = bag[i];
    if (!row) {
      row = { messages: [], fields: [], outside: [] };
      bag[i] = row;
    }
    return row;
  };
  const mark = (
    bag: Record<number, BotProfileRowError>,
    i: number,
    field: BotProfileField,
  ) => {
    const row = rowOf(bag, i);
    if (!row.fields.includes(field)) row.fields.push(field);
    return row;
  };
  const fail = (
    bag: Record<number, BotProfileRowError>,
    i: number,
    path: string,
    field: BotProfileField,
    message: string,
  ) => {
    mark(bag, i, field).messages.push(message);
    out.problems.push({ path: `${path}.${field}`, message });
  };
  /**
   * A key listed twice — the problem on the duplicate's own path, exactly as
   * the server names it, but the ROW error on the FIRST rule for that key.
   *
   * That is the one the editor draws: its checkbox and its boxes are bound to
   * the first index, and a message parked on an index no row reads leaves the
   * save bar counting a field nothing on screen is red about. Reachable only
   * through the JSON view — which is precisely the session that needs to be
   * told which row it is.
   */
  const duplicate = (
    bag: Record<number, BotProfileRowError>,
    drawnAt: number,
    path: string,
    key: string,
  ) => {
    const message = `“${key}” is listed twice`;
    mark(bag, drawnAt, "key").messages.push(message);
    out.problems.push({ path: `${path}.key`, message });
  };
  const negative = (v?: number) => v !== undefined && v < 0;
  const backwards = (lo?: number, hi?: number) =>
    lo !== undefined && hi !== undefined && lo > hi;
  const fraction = (...vs: (number | undefined)[]) =>
    vs.some((v) => v !== undefined && !Number.isInteger(v));

  // Every key's type is the project's own schema's. `score` used to be the
  // exception — an int whatever the schema said, because it was the
  // players.score column — and since 0054 dropped that column it is read from
  // the schema like any other stat.
  const statType = new Map<string, string>();
  for (const field of schemas?.stats ?? []) {
    const key = field.key.trim();
    if (key) statType.set(key, field.type);
  }
  const metaByKey = new Map<string, MetaSchemaField>();
  for (const field of schemas?.meta ?? []) {
    const key = field.key.trim();
    if (key) metaByKey.set(key, field);
  }

  const statFirst = new Map<string, number>();
  bots.profile.stats.forEach((rule, i) => {
    const path = `bots.profile.stats[${i}]`;
    const bad = (field: BotProfileField, message: string) =>
      fail(out.profileStats, i, path, field, message);
    const type = schemas ? statType.get(rule.key) : undefined;
    if (schemas && !STAT_RULE_TYPES.includes(type ?? "")) return;
    const first = statFirst.get(rule.key);
    if (first !== undefined) {
      duplicate(out.profileStats, first, path, rule.key);
      return;
    }
    statFirst.set(rule.key, i);

    if (!schemas || type === "int") {
      // Whether a box is REQUIRED is a question only the type answers, so it
      // is asked only with the schema in hand: without it a bool rule and an
      // int rule with an empty range are the same three keys.
      const start =
        type === "int"
          ? missingBound(START_RANGE, rule.start_min, rule.start_max)
          : undefined;
      if (fraction(rule.start_min, rule.start_max)) {
        bad("start", NOT_WHOLE);
      } else if (start) {
        bad("start", start);
      } else if (negative(rule.start_min) || negative(rule.start_max)) {
        bad("start", NEGATIVE);
      } else if (backwards(rule.start_min, rule.start_max)) {
        bad("start", RANGE_BACKWARDS);
      }
    }
    if (!schemas || type === "bool") {
      checkChance(rule.true_percent, type === "bool", bad);
    }
    // A `string` stat draws from a pool like a meta string — minus the
    // allow-list, which `stats_schema` has no field for at all, so no value of
    // one can ever fall outside a catalogue.
    if (hasPool(type, rule.values, schemas, "string")) {
      checkPool(rule.values ?? [], (message) => bad("values", message));
    }
  });

  const metaFirst = new Map<string, number>();
  bots.profile.meta.forEach((rule, i) => {
    const path = `bots.profile.meta[${i}]`;
    const bad = (field: BotProfileField, message: string) =>
      fail(out.profileMeta, i, path, field, message);
    const field = schemas ? metaByKey.get(rule.key) : undefined;
    const type = field?.type;
    if (schemas && !META_RULE_TYPES.includes(type ?? "")) return;
    const first = metaFirst.get(rule.key);
    if (first !== undefined) {
      duplicate(out.profileMeta, first, path, rule.key);
      return;
    }
    metaFirst.set(rule.key, i);

    // The numeric forms are the stats collection's: the form belongs to the
    // TYPE, and meta_schema declares ints and bools too. Neither carries a
    // pool, and neither grows — a cosmetic that climbs is a counter, and a
    // counter belongs in stats.
    const pool = hasPool(type, rule.values, schemas, "string", "string[]");
    if (!schemas || type === "int") {
      const start =
        type === "int"
          ? missingBound(START_RANGE, rule.start_min, rule.start_max)
          : undefined;
      if (fraction(rule.start_min, rule.start_max)) {
        bad("start", NOT_WHOLE);
      } else if (start) {
        bad("start", start);
      } else if (negative(rule.start_min) || negative(rule.start_max)) {
        bad("start", NEGATIVE);
      } else if (backwards(rule.start_min, rule.start_max)) {
        bad("start", RANGE_BACKWARDS);
      }
    }
    if (!schemas || type === "bool") {
      checkChance(rule.true_percent, type === "bool", bad);
    }
    if (!pool) return;

    const values = rule.values ?? [];
    checkPool(values, (message) => bad("values", message));
    // «None» is what the count expresses for a list, so an empty string inside
    // one would be a badge with no name.
    if (type === "string[]" && values.some((v) => v.value === "")) {
      bad("values", EMPTY_IN_LIST);
    }

    // An allow-list that is empty accepts anything, so only a narrowed one can
    // strand a value. The empty value is "do not give this bot the key at all"
    // and is never in the list.
    const allowed = field?.allowed ?? [];
    if (allowed.length > 0) {
      for (const { value } of values) {
        if (value === "" || allowed.includes(value)) continue;
        mark(out.profileMeta, i, "values").outside.push(value);
        out.problems.push({
          path: `${path}.values`,
          message: `“${value}” ${OUTSIDE_ALLOW_LIST}`,
        });
      }
    }

    if (!schemas || type === "string[]") {
      const count =
        type === "string[]"
          ? missingBound(COUNT, rule.count_min, rule.count_max)
          : undefined;
      if (fraction(rule.count_min, rule.count_max)) {
        bad("count", NOT_WHOLE);
      } else if (count) {
        bad("count", count);
      } else if (negative(rule.count_min) || negative(rule.count_max)) {
        bad("count", NEGATIVE);
      } else if (backwards(rule.count_min, rule.count_max)) {
        bad("count", RANGE_BACKWARDS);
      }
    }
  });
}

/**
 * A bool rule's chance: present at all, whole, and a percentage.
 *
 * `required` is what the schema says. A ticked `bool` key with no chance hands
 * every bot `false` on the strength of a box the operator had simply not
 * reached yet, so the server refuses it — while without the schema nothing is
 * known to be a bool, and a rule carrying no percentage is just a rule of
 * another type.
 */
function checkChance(
  value: number | undefined,
  required: boolean,
  bad: (field: BotProfileField, message: string) => void,
) {
  if (value === undefined) {
    if (required) bad("true_percent", NO_CHANCE);
    return;
  }
  if (!Number.isInteger(value)) {
    bad("true_percent", NOT_WHOLE);
  } else if (!(value >= 0 && value <= 100)) {
    bad("true_percent", `${value} is not a percentage between 0 and 100`);
  }
}

function validateBands(bots: BotsConfig, out: BotsErrors) {
  const parsed = bots.time_of_day.map((b, i) => ({
    index: i,
    from: parseClock(b.from),
    // Midnight is the only way to write the END of a day, so "22:00"–"00:00"
    // has to mean 22:00–24:00.
    to: parseClock(b.to) === 0 ? MINUTES_PER_DAY : parseClock(b.to),
  }));
  const broken = parsed.filter((p) => p.from === null || p.to === null);
  if (broken.length > 0) {
    out.timeOfDay = "Every band needs a HH:MM start and end.";
    out.bandRows = broken.map((p) => p.index);
    return;
  }
  const backwards = parsed.filter((p) => (p.to as number) <= (p.from as number));
  if (backwards.length > 0) {
    out.timeOfDay = "A band has to move forward in time.";
    out.bandRows = backwards.map((p) => p.index);
    return;
  }
  const negative = bots.time_of_day
    .map((b, i) => ({ b, i }))
    .filter(({ b }) => !(b.factor >= 0));
  if (negative.length > 0) {
    out.timeOfDay = "A factor cannot be negative.";
    out.bandRows = negative.map(({ i }) => i);
    return;
  }

  const sorted = [...parsed].sort((a, b) => (a.from as number) - (b.from as number));
  let cursor = 0;
  for (const p of sorted) {
    const from = p.from as number;
    const to = p.to as number;
    if (from > cursor) {
      out.gaps.push({
        from: formatClock(cursor),
        to: formatClock(from),
        kind: "gap",
        before: p.index,
      });
      out.bandRows.push(p.index);
    } else if (from < cursor) {
      out.gaps.push({
        from: formatClock(from),
        to: formatClock(cursor),
        kind: "overlap",
        before: p.index,
      });
      out.bandRows.push(p.index);
    }
    if (to > cursor) cursor = to;
  }
  if (cursor < MINUTES_PER_DAY) {
    out.gaps.push({
      from: formatClock(cursor),
      to: "24:00",
      kind: "gap",
      before: bots.time_of_day.length,
    });
  }
  if (out.gaps.length > 0) {
    const first = out.gaps[0];
    out.timeOfDay =
      first.kind === "gap"
        ? `${first.from}–${first.to} is not covered. Bands must tile the full 24 hours with no gaps and no overlaps.`
        : `${first.from}–${first.to} is covered by two bands. Bands must tile the full 24 hours with no gaps and no overlaps.`;
  }
}

/** Does the section hold anything that blocks a save? */
export function hasBotsErrors(errors: BotsErrors): boolean {
  return !!errors.profiles || !!errors.timeOfDay || errors.problems.length > 0;
}

/**
 * The warnings — worth saying, never worth refusing a save over.
 *
 * A project with no `leader` profile still works: the platform promotes the
 * most talkative bot it has. Worth knowing, though, since the numbers the
 * operator set will then not be the numbers they get.
 */
export function botsWarnings(bots: BotsConfig, chatHistoryWindow?: number): string[] {
  const out: string[] = [];
  if (!bots.profiles.some((p) => p.key.trim() === "leader")) {
    out.push(
      "No `leader` profile. Every seeded guild needs one talkative bot, otherwise its chat goes quiet for hours — the platform promotes the most active bot instead.",
    );
  }
  if (bots.simulation.awake_window_minutes === 0) {
    out.push(
      "Awake window is 0: every seeded guild is simulated around the clock, as the original design document describes. On a project with many guilds this is the setting that costs the most.",
    );
  }
  if (
    bots.backfill.enabled &&
    chatHistoryWindow !== undefined &&
    chatHistoryWindow > 0 &&
    bots.backfill.max_messages > chatHistoryWindow
  ) {
    out.push(
      `Backfill writes up to ${bots.backfill.max_messages} messages but chat.history_window is ${chatHistoryWindow}, so at most ${chatHistoryWindow} survive — the trim worker deletes the rest the same day.`,
    );
  }
  return out;
}

/**
 * The problems the backend attached to a refused PATCH.
 *
 * The client checks the same rules, so a well-formed session never sees these.
 * They exist for the session that is not well formed — an older tab, a config
 * edited in the JSON view, a rule added on the server first — where the
 * alternative is «Something went wrong. Please try again.» over a save that
 * will never succeed.
 */
export function botsProblemsOf(err: unknown): BotsProblem[] {
  const details = (err as { details?: unknown })?.details;
  if (!details || typeof details !== "object") return [];
  const problems = (details as { problems?: unknown }).problems;
  if (!Array.isArray(problems)) return [];
  return problems.filter(
    (p): p is BotsProblem =>
      !!p &&
      typeof p === "object" &&
      typeof (p as BotsProblem).path === "string" &&
      typeof (p as BotsProblem).message === "string",
  );
}
